import { Injectable, signal } from '@angular/core';
import { authClient } from './auth.client.js';

export interface AuthUser {
  id: string;
  email: string;
  name: string;
}

export interface AuthOrg {
  id: string;
  name: string;
  slug: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  readonly isLoading = signal(false);
  readonly user = signal<AuthUser | null>(null);
  readonly activeOrg = signal<AuthOrg | null>(null);

  async init(): Promise<void> {
    const { data } = await authClient.getSession();
    if (!data?.user) return;

    this.user.set({ id: data.user.id, email: data.user.email, name: data.user.name });
    await this._resolveActiveOrg(
      (data.session as { activeOrganizationId?: string })?.activeOrganizationId ?? null,
    );
  }

  async signIn(email: string, password: string): Promise<{ error?: string }> {
    this.isLoading.set(true);
    try {
      const { data, error } = await authClient.signIn.email({ email, password });
      if (error) return { error: error.message ?? 'Innlogging mislyktes' };
      if (data?.user) {
        this.user.set({ id: data.user.id, email: data.user.email, name: data.user.name });
        await this._resolveActiveOrg(null);
      }
      return {};
    } finally {
      this.isLoading.set(false);
    }
  }

  async signOut(): Promise<void> {
    await authClient.signOut();
    this.user.set(null);
    this.activeOrg.set(null);
  }

  isAuthenticated(): boolean {
    return this.user() !== null;
  }

  private async _resolveActiveOrg(activeOrgId: string | null): Promise<void> {
    if (activeOrgId) {
      const { data } = await authClient.organization.getFullOrganization({
        query: { organizationId: activeOrgId },
      });
      if (data) this.activeOrg.set({ id: data.id, name: data.name, slug: data.slug });
      return;
    }

    // No active org in session — list memberships and auto-set if only one
    const { data: orgs } = await authClient.organization.list();
    if (orgs?.length) {
      const org = orgs[0];
      await authClient.organization.setActive({ organizationId: org.id });
      this.activeOrg.set({ id: org.id, name: org.name, slug: org.slug });
    }
  }
}
